import { get } from 'lodash-es';
import { route as laravelRoute } from 'ziggyRoute';
import { clientApi } from '@phoenix/clientApi';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import { newComClient } from '@module/NewCom/resources/assets/js/api/NewComClient';
import type { LegacyCart } from '@module/NewCom/resources/assets/js/api/types';

interface AddReferenceResult {
  cart: LegacyCart;
  cartProductsRemoved: boolean;
}

export class AddReferenceError extends Error {
  constructor(
    message: string,
    readonly cartProductsRemoved: boolean,
  ) {
    super(message);
    this.name = 'AddReferenceError';
  }
}

class CartWorkflowHandler {
  isNewComEnabled(): boolean {
    return getFeatureFlag(FEATURE_FLAG.NEWCOM_CHECKOUT);
  }

  async addReference(reference: string): Promise<AddReferenceResult> {
    try {
      if (this.isNewComEnabled()) {
        return {
          cart: await newComClient.addCartLineItem(reference, 1),
          cartProductsRemoved: false,
        };
      }

      const { data } = await clientApi.post(laravelRoute('api.cart.addReference', { reference }));

      if (!data.status) {
        throw new Error(data.message ?? '');
      }

      return {
        cart: data.data.cart,
        cartProductsRemoved: Boolean(data.data.cart_products_removed),
      };
    } catch (error) {
      const normalizedError = CartWorkflowHandler.toError(error);

      throw new AddReferenceError(
        normalizedError.message,
        Boolean(get(error, ['response', 'data', 'data', 'cart_products_removed'])),
      );
    }
  }

  async fetchCart(): Promise<LegacyCart> {
    try {
      if (this.isNewComEnabled()) {
        return await newComClient.getCart();
      }

      const { data } = await clientApi.get(laravelRoute('api.cart.preview'));

      return data.data.cart;
    } catch (error) {
      throw CartWorkflowHandler.toError(error);
    }
  }

  async removeItem(item: { id: string | number }): Promise<LegacyCart> {
    try {
      if (this.isNewComEnabled()) {
        return await newComClient.removeCartLineItem(String(item.id));
      }

      const { data } = await clientApi.delete(laravelRoute('api.cart.reservations.product.destroy', { id: item.id }));

      if (!data.status) {
        throw new Error(data.message ?? '');
      }

      return data.data.cart;
    } catch (error) {
      throw CartWorkflowHandler.toError(error);
    }
  }

  private static toError(error: unknown): Error {
    const responseMessage = get(error, ['response', 'data', 'message']);

    if (typeof responseMessage === 'string' && responseMessage !== '') {
      return new Error(responseMessage);
    }

    if (error instanceof Error && error.message !== '') {
      return error;
    }

    return new Error('');
  }
}

export const cartWorkflowHandler = new CartWorkflowHandler();
